import React from 'react';
import { dayjs } from 'shared/utils/date';
import { Link } from 'react-router-dom';
import { IntlProvider, FormattedMessage, FormattedDate } from 'react-intl';
import globalMessages from 'translations/messages';
import { useQuery } from 'hooks/apollo';
import { formatMainDateRange } from 'shared/constants/date';

import NoMatch from 'containers/NoMatch';
import translations from 'translations';
import { Empty, Descriptions } from 'antd';
import { PageHeader } from '@ant-design/pro-layout';
import Page from 'components/Page';
import ExtellioLogo from 'components/ExtellioLogo';
import SequentialLoadingList from 'components/SequentialLoadingList';
import ResultContent from 'containers/Results/ResultsPage/ResultContent';
import messages from '../messages';

import './previewReport.less';
import reportQuery from './reportQuery.gql';

const PreviewReport = ({ match, isPrint }) => {
  const dateParam = match.params.date && dayjs(match.params.date).utc(true);
  const viewDate = dateParam && dateParam.isValid()
    ? dateParam.toDate()
    : dayjs().startOf('day');

  const id = match.params.reportId;
  const { loading, data: { report } = {} } = useQuery(reportQuery, {
    variables: { id, viewDate, format: isPrint ? 'PDF' : null },
  });

  if (loading) return null;

  if (!report) {
    return (
      <IntlProvider locale="en-GB" key="en-GB" messages={translations['en-GB']}>
        <NoMatch />
      </IntlProvider>
    );
  }

  const { customer, content } = report;
  const { title } = report;
  const hasContent = content.length > 0;

  const distribution = (report.distributions.length && report.distributions[0]) || {};
  const { formattedTitle } = distribution;

  const breadcrumbsConfig = {};

  if (!isPrint) {
    breadcrumbsConfig.items = [
      { path: '/reports', breadcrumbName: <FormattedMessage {...globalMessages.reports} /> },
      { path: `/reports/${report.id}/edit`, breadcrumbName: title },
      { path: 'here', breadcrumbName: <FormattedMessage {...messages.preview} /> },
    ];
    breadcrumbsConfig.itemRender = (route, params, routes) => (
      routes.indexOf(route) === routes.length - 1
        ? route.breadcrumbName
        : <Link to={route.path}>{route.breadcrumbName}</Link>
    );
  }

  // dayjs.locale(report.language);

  return (
    <IntlProvider locale={report.language} key={report.language} messages={translations[report.language]}>
      <Page className="preview-report">
        <PageHeader
          className="preview-report__header"
          breadcrumb={breadcrumbsConfig}
          title={(
            <>
              {customer.logo && (
                <img
                  className="preview-report__header-logo"
                  src={customer.logo.url}
                  alt={customer.displayName}
                />
              )}
              {formattedTitle || title}
            </>
          )}
        >
          <Descriptions size="small" column={2}>
            <Descriptions.Item key="1" label={<FormattedMessage {...messages.reportCreated} />}>
              <FormattedDate value={viewDate} weekday="long" day="numeric" month="long" year="numeric" />
            </Descriptions.Item>
            <Descriptions.Item key="2" label={<FormattedMessage {...messages.dataCollected} />}>
              <FormattedMessage {...globalMessages.week}>
                {week => formatMainDateRange(dayjs, report.dateRange, viewDate, { week, locale: report.language })}
              </FormattedMessage>
            </Descriptions.Item>
          </Descriptions>
          {report.description && (
            <div className="preview-report__header-description">
              {report.description}
            </div>
          )}
        </PageHeader>
        <div className="preview-report__content">
          {!hasContent
            && (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={<FormattedMessage {...messages.reportContentEmptyMessage} />}
              />
            )}
          <SequentialLoadingList lazyLoad={!isPrint} height={382}>
            {content.map(item => (
              <ResultContent
                key={item.id}
                title={item.title}
                dateRange={item.filter?.dateRange || report.dateRange}
                surveyGroupId={item.filter?.surveyGroupId}
                dataSourceId={item.filter?.dataSource?.id}
                filterSegments={item.filter?.filterSegments}
                benchmarkSegments={item.filter?.benchmarkSegments}
                contentSettings={item.contentSettings}
                chartSettings={item.chartSettings}
                language={report.language}
                viewDate={viewDate}
                timeOffset={report.timeOffset}
                isPrint={isPrint}
                isReport
                allowPersonalData={customer.allowPersonalData}
              />
            ))}
          </SequentialLoadingList>
        </div>
        <div className="preview-report__footer">
          <ExtellioLogo />
        </div>
      </Page>
    </IntlProvider>
  );
};

export default PreviewReport;
